import Map from 'eemovel-map-library/src/data/google-maps/map';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddAlert,
  Button,
  IcoArrowBottom,
  IconPentagon,
} from '../../../component';
import { IconFreeHand } from '../../../component/icon/free-hand';
import { IcoInfluenceRadius } from '../../../component/icon/influence-radius';
import { IconPolygon } from '../../../component/icon/polygon';
import ActionOption from './actionOption';
import DefineRadius from '../defineRadius';
import {
  changeSearch,
  modalOpen,
  setIsActive,
} from '../redux/dashboardActions';
import { setSelectedSwitch } from '../polygons/redux/polygonsActions';
import { setIsPressed } from '../dataLayers/redux/dataLayers.actions';
import SearchAddress from '../../mapSearch/searchAddress';
import {
  cleaningMap,
  limparActionMap,
} from './controllers/actionsMap.controllers';
import { buildCoordinates } from '../../../shared/utils';
import { usePoligonOnMap } from '../../../shared/hooks/usePoligonOnMap';

type ActionsBtnSelected = {
  polygon: boolean;
  freeHand: boolean;
  radius: boolean;
};

type ActionsBtn = {
  id: string;
  label: string;
  type: keyof ActionsBtnSelected;
  action: () => void;
  icon: JSX.Element;
  class: string;
};

export default function ActionsMap() {
  const dispatch = useDispatch();
  const { preference, profileRules } = useSelector(
    (state: any) => state.layoutState
  );
  const { search } = useSelector((state: any) => state.dashboardState);
  const { isPressed } = useSelector((state: any) => state.dataLayersState);
  const nls = require(`../nls/${preference.language.id}.json`);
  const { mapAction } = useSelector(
    (state: any) => state.dashboardState.isActive
  );
  const { hasAccessGeneralMarkets } = profileRules;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const initialStateBtnSelected: ActionsBtnSelected = {
    polygon: false,
    freeHand: false,
    radius: false,
  };
  const [btnSelected, setBtnSelected] = useState<ActionsBtnSelected>({
    ...initialStateBtnSelected,
    polygon: true,
  });
  const [isRadiusOpen, setIsRadiusOpen] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<number>(Map.circleRadius);
  const { isPoligonOnMap, isCircleOnMap } = usePoligonOnMap();
  const hasPoligon = isPoligonOnMap || isCircleOnMap;
  useEffect(() => {
    if (hasPoligon) {
      dispatch(
        setIsActive({
          name: 'mapAction',
          value: '',
        })
      );
    }
  }, [Map.PoligonOnMap, dispatch]);

  const handleCloseRadiusSizer = () => {
    dispatch(setIsActive({ name: 'mapAction', value: '' }));
    setIsRadiusOpen(false);
  };

  const handleApplyRadiusSizer = useCallback(() => {
    Map.setCircleRadius(userInput);
    handleCloseRadiusSizer();
  }, [userInput]);

  const actionsMapa: ActionsBtn[] = !hasAccessGeneralMarkets
    ? [
        {
          id: '4',
          label: nls.actionsMap.actionsBtn.polygon,
          type: 'polygon',
          icon: (
            <IconPolygon cy="Map" width="1em" height="1em" fill="#FFFFFF" />
          ),
          class: '',
          action: () => {
            dispatch(setSelectedSwitch(''));
            cleaningMap();
            Map.drawPolygon(() => {
              limparActionMap();
              dispatch(setIsPressed(false));
            });
          },
        },
        {
          id: '5',
          label: nls.actionsMap.actionsBtn.freeHand,
          type: 'freeHand',
          icon: (
            <IconFreeHand cy="Map" width="1em" height="1em" fill="#FFFFFF" />
          ),
          class: '',
          action: () => {
            dispatch(setSelectedSwitch(''));
            cleaningMap();
            Map.setCircleRadius(0);
            Map.drawFreeHand(false, () => {
              limparActionMap();
              dispatch(setIsPressed(false));
            });
          },
        },
        {
          id: '6',
          type: 'radius',
          label: nls.actionsMap.actionsBtn.radius,
          icon: <IcoInfluenceRadius cy="Map" />,
          class: '',
          action: () => {
            dispatch(setSelectedSwitch(''));
            if (search.isFetched && !isRadiusOpen) {
              setIsRadiusOpen(true);
            } else if (isRadiusOpen) {
              setIsRadiusOpen(false);
            } else {
              cleaningMap();
              dispatch(setIsPressed(false));
              dispatch(AddAlert('warning', nls.actionsMap.toast.warning));
            }
          },
        },
      ]
    : [
        {
          id: '4',
          label: nls.actionsMap.actionsBtn.polygon,
          type: 'polygon',
          icon: (
            <IconPolygon cy="Map" width="1em" height="1em" fill="#FFFFFF" />
          ),
          class: '',
          action: () => {
            dispatch(setSelectedSwitch(''));
            cleaningMap();
            Map.setCircleRadius(0);
            Map.drawPolygon(() => {
              limparActionMap();
              dispatch(setIsPressed(false));
            });
          },
        },
        {
          id: '5',
          label: nls.actionsMap.actionsBtn.freeHand,
          type: 'freeHand',
          icon: (
            <IconFreeHand cy="Map" width="1em" height="1em" fill="#FFFFFF" />
          ),
          class: '',
          action: () => {
            dispatch(setSelectedSwitch(''));
            cleaningMap();
            Map.drawFreeHand(false, () => {
              limparActionMap();
              dispatch(setIsPressed(false));
            });
          },
        },
      ];

  const actionsAvailable: ActionsBtn[] = useMemo(() => {
    let key: keyof ActionsBtnSelected;
    const actionsTypeNotChosen: string[] = [];
    for (key in btnSelected) {
      if (!btnSelected[key]) {
        actionsTypeNotChosen.push(key);
      }
    }
    const actionsLeft = actionsMapa.filter((actionMap) => {
      return actionsTypeNotChosen.includes(actionMap.type);
    });
    return actionsLeft;
  }, [btnSelected, hasAccessGeneralMarkets]);

  const actionExecute = (item: ActionsBtn) => {
    actionsMapa.forEach((element) => {
      if (element.id === item.id) {
        if (mapAction === item.id) {
          Map.clearMap();
          dispatch(setIsPressed(false));
          if (item.id === '6') {
            cleaningMap();
            limparActionMap();
            dispatch(changeSearch({ name: 'isFetched', value: false }));
            setIsRadiusOpen(false);
          }
        } else {
          element.action();
        }
      }
    });

    dispatch([
      setIsActive({
        name: 'mapAction',
        value: mapAction !== item.id ? item.id : '',
      }),
    ]);
  };

  const isPressedRadiusBtn = () => {
    if (!search.isFetched) {
      dispatch(setIsPressed(false));
    } else if (isRadiusOpen) {
      dispatch(setIsPressed(false));
    } else {
      dispatch(setIsPressed(true));
    }
  };

  const closeRadiusSizerAction = useCallback(() => {
    setIsRadiusOpen(false);
    dispatch(setIsPressed(false));
    cleaningMap();
    limparActionMap();
    dispatch(changeSearch({ name: 'isFetched', value: false }));
  }, []);

  const actionOptionAction = useCallback(
    (act: ActionsBtn) => {
      dispatch(setIsPressed(true));
      actionExecute(act);
      setIsOpen(false);
      setBtnSelected({
        ...initialStateBtnSelected,
        [act.type]: !btnSelected[act.type],
      });
    },
    [btnSelected, search]
  );

  const actionsBtnText = () => {
    let key: keyof ActionsBtnSelected;
    for (key in btnSelected) {
      if (btnSelected[key]) {
        const selectedAction = actionsMapa.filter((actionMap: ActionsBtn) => {
          return actionMap.type === key;
        })[0];
        return (
          <span
            className="map-selected"
            onClick={() => {
              actionExecute(selectedAction);
              key === 'radius'
                ? isPressedRadiusBtn()
                : dispatch(setIsPressed(!isPressed));
            }}
          >
            {selectedAction?.icon ? selectedAction.icon : null}
            <span>{selectedAction?.label}</span>
          </span>
        );
      }
    }
  };

  const onCleanMapButtonClick = () => {
    dispatch(setIsPressed(false));
    cleaningMap();
    limparActionMap();
    dispatch(changeSearch({ name: 'isFetched', value: false }));
  };

  return (
    <div className="menu-mapa">
      <SearchAddress />
      <div className="button-container">
        <div>
          <Button
            className={`actions-btn ${isPressed ? 'pressed' : ''}`}
            cy="OpenSelect"
          >
            {actionsBtnText()}
            <span
              className={`handle-map-selected-open ${
                isOpen ? 'open' : 'closed'
              }`}
              onClick={(e) => setIsOpen(!isOpen)}
            >
              <IcoArrowBottom
                cy="ActionsMapIconBottom"
                style={{ fontSize: 18 }}
              />
            </span>
          </Button>
          {isOpen ? (
            <div className={'select-box select-actions'}>
              {actionsAvailable?.map((act) => {
                return (
                  <ActionOption
                    key={act.id}
                    label={act.label}
                    icon={act.icon}
                    action={() => {
                      actionOptionAction(act);
                    }}
                  />
                );
              })}
            </div>
          ) : null}
        </div>

        {isRadiusOpen ? (
          <DefineRadius
            closeBtnAction={closeRadiusSizerAction}
            inputValue={userInput}
            inputAction={setUserInput}
            applyBtnAction={handleApplyRadiusSizer}
          />
        ) : null}
        <Button
          color="danger"
          cy="ActionsSavePolygonBtn"
          className="btn normal success save-polygon"
          onClick={() => {
            if (!buildCoordinates()) {
              dispatch(
                AddAlert('warning', nls.actionsMap.mustDrawPolygonFirst)
              );
            } else {
              dispatch(modalOpen('save polygons'));
            }
          }}
        >
          <IconPentagon cy="SavePolygonBtn" />
          {nls.actionsMap.savePolygonsBtn}
        </Button>
        {hasPoligon && (
          <Button
            color="danger"
            cy="ActionsMapCleanMapBtn"
            className="btn normal danger clear-map"
            onClick={onCleanMapButtonClick}
          >
            {nls.actionsMap.clearMapBtn}
          </Button>
        )}
      </div>
    </div>
  );
}
